import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useEffect, useRef } from "react";
import { useMarkReadSupportTicketAction } from "../actions/use-support-ticket-actions";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

/**
 * Hook that automatically marks a support ticket as read when it's first viewed.
 */
export function useMarkSupportTicketAsRead(
  supportTicketId: SupportTicketId,
): void {
  const markReadAction = useMarkReadSupportTicketAction();
  const lastSupportTicketIdRef = useRef<SupportTicketId | null>(null);
  const collections = useGlobalSupportTicketCollections();
  const supportTicket = useLiveQuery(
    (q) =>
      q
        .from({ c: collections.supportTicketsList.collection })
        .where(({ c }) => eq(c.id, supportTicketId))
        .findOne(),
    [supportTicketId],
  ).data;

  useEffect(() => {
    if (
      supportTicket &&
      supportTicket.readAt === null &&
      lastSupportTicketIdRef.current !== supportTicketId
    ) {
      void markReadAction.execute(supportTicketId);
      lastSupportTicketIdRef.current = supportTicketId;
    }
  }, [supportTicket, supportTicketId, markReadAction]);
}
